import { Component, Input, Output, EventEmitter, SimpleChanges, ChangeDetectorRef, ChangeDetectionStrategy, OnDestroy, OnInit, OnChanges } from '@angular/core';
import { IAction, IWidgetGroupTag } from '../../../../redux/timeline-data.model';
import { InteractivityActionService }
	from '../../../../../../../components/shared/interactivity-editor/services/interactivity-action.service';
import { bbGenerateShortId } from '../../../../../../../shared/utils/bbGenerateShortId.util';
import { TranslateService } from '@ngx-translate/core';
import { InteractivityEditorService } from '../../../../../../../components/shared/interactivity-editor/services/interactivity-editor.service';
import { Subscription, debounceTime, Subject } from 'rxjs';
import { OverlayService } from '../../../../../../../services/overlay.service';
import { InteractivityDataSourceService } from '../../../../../../../components/shared/interactivity-editor/services/interactivity-data-source.service';
import { EventService } from '../../../../../../../services/event-service';

type ActionParameterValueData = {
	value: string;
	hasDatasource: boolean;
};

interface IActionListItem {
	label: string;
	value: string;
	entity?: string;
	icon?: string;
	commands?: string[];
	selected?: boolean;
	shortText?: string;
}

@Component({
	selector: 'action',
	templateUrl: './action.component.html',
	styleUrls: ['./action.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})

export class ActionComponent implements OnInit, OnChanges, OnDestroy {

	@Input() action: IAction;
	@Input() stateTrackingUid: string;
	@Input() theme: string;
	@Output() delete: EventEmitter<IAction>;
	@Output() duplicate: EventEmitter<IAction>;
	@Output() actionChanged: EventEmitter<null>;

	subCategoryList: IActionListItem[] = [];
	selectedSubCategory: IActionListItem;
	radioOptions: IActionListItem[] = [];
	selectedRadioOption: IActionListItem;
	selectedRadioValue: string;
	parameterComponents: any[] = [];
	categoryActionMap: any;
	category: string;
	shortText: string;
	closedTextValue: any;
	valueHasDataSource: boolean;
	closedTextProperty: any;
	propertyHasDataSource: boolean;
	closedTextCommand: string;
	languageOptions: any[] = [];
	entityLabelsByExtendedId: any = {};
	actionText = '';
	allWidgetTags: any[] = [];
	commandComponent: any = null;

	componentDestroyed = false;

	subscriptions = new Subscription();
	stateChanges = new Subject<void>();

	constructor(
		private actionService: InteractivityActionService,
		private interactivityEditorService: InteractivityEditorService,
		protected overlayService: OverlayService,
		private translate: TranslateService,
		private eventService: EventService,
		private changeDetector: ChangeDetectorRef,
		private dataSourceService: InteractivityDataSourceService
	) {
		this.delete = new EventEmitter<IAction>();
		this.duplicate = new EventEmitter<IAction>();
		this.actionChanged = new EventEmitter<null>();
		this.subscriptions.add(this.interactivityEditorService.publicationLanguagesSubject.subscribe((publicationLanguages: any[]) => {
			this.languageOptions = publicationLanguages.map((publicationLanguage) => ({
				label: publicationLanguage.name,
				value: publicationLanguage.isocode,
				// value: publicationLanguage.id,
				data: publicationLanguage,
				aliases: [publicationLanguage.isocode]
			}));
			this.stateChanges.next();
		}));
		this.subscriptions.add(this.interactivityEditorService.projectContextOptionsChange.subscribe((templateProject: any) => {
			this.stateChanges.next();
		}));

		this.subscriptions.add(
			this.interactivityEditorService.groupTagSubject.subscribe((groupTags: IWidgetGroupTag[]) => {
				if (groupTags) {
					this.allWidgetTags = groupTags.map((groupTag) => ({
						label: groupTag.label,
						value: groupTag.id
					}));
					this.stateChanges.next();
				}
			}));

		this.entityLabelsByExtendedId = this.actionService.entityLabelsByExtendedId; // ref
		this.propertyHasDataSource = false;
		this.valueHasDataSource = false;

		// for change detection of selected items
		this.subscriptions.add(this.eventService.documentMouseUpEvent.subscribe((evt: MouseEvent) => {
			this.stateChanges.next();
		}));
	}

	ngOnInit() {
		this.stateChanges.pipe(debounceTime(5))
			.subscribe(() => {
				if (!this.componentDestroyed) {
					this.changeDetector.detectChanges();
				}
			});
		if (!this.action.id) {
			this.action.id = bbGenerateShortId();
		}
		if (this.action && this.action.command !== undefined && this.action.entity !== undefined) {
			this.updateOptions(true);
		} else {
			this.stateChanges.next();
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.action &&
			((changes.action.previousValue && changes.action.currentValue) &&
				(
					changes.action.previousValue.command !== changes.action.currentValue.command ||
					changes.action.previousValue.entity !== changes.action.currentValue.entity ||
					(
						changes.action.previousValue.params && changes.action.currentValue.params &&
						changes.action.previousValue.params.length !== changes.action.currentValue.params.length
					)
				)
			)
		) {
			this.updateOptions(true);
		} else {
			this.getClosedText();
		}

		// close any open modals:
		if (this.dataSourceService && this.dataSourceService.closeModal) {
			this.dataSourceService.closeModal();
		}
		this.stateChanges.next();
	}

	ngOnDestroy() {
		this.componentDestroyed = true;
		this.subscriptions.unsubscribe();
	}

	paramComponentsTrackBy(ind: number) {
		return (this.parameterComponents && this.parameterComponents[ind] && this.parameterComponents[ind].paramIndex) || ind;
	}

	setSelected(val: boolean) {
		if (val) {
			this.actionService.deselectAllActions();
			this.actionService.selectAction(this.action.id);
		} else {
			this.actionService.deselectAction(this.action.id);
		}
		this.stateChanges.next();
	}

	isSelected() {
		return this.actionService.isActionSelected(this.action.id);
	}

	log(something: any) {
		console.warn('Action LOG: ', something);
	}

	onContentMouseDown(ev: MouseEvent) {
		ev.stopPropagation();
	}

	onDataSourceChanged(): void {
		this.dispatchChange();
	}

	onContentChange(propertyChanged: boolean) {
		this.getClosedText();
		this.updateOptions(true, propertyChanged);
		this.dispatchChange();
	}

	onRadioChange(option: IActionListItem) {
		this.selectedRadioOption = option;
		const sameEntity = this.selectedRadioOption.entity === this.action.entity;
		if (this.selectedRadioOption.entity) {
			this.action.entity = this.selectedRadioOption.entity;
		}

		if (this.selectedRadioOption.commands && this.selectedRadioOption.commands.length) {
			this.action.command = this.selectedRadioOption.commands[0];
		}
		this.updateOptions(sameEntity);
		this.dispatchChange();
	}

	onCategoryClicked(cat: IActionListItem) {
		this.selectedRadioOption = null;
		this.selectSubCategory(cat);
	}

	selectSubCategory(value: IActionListItem) {
		this.selectedSubCategory = value;
		this.action.entity = value.entity || value.value;
		if (value.commands && value.commands.length) {
			this.action.command = value.commands[0];
		}
		this.updateOptions();
		this.dispatchChange();
	}

	duplicateAction(): void {
		this.duplicate.emit(this.action);
	}

	deleteAction(): void {
		this.delete.emit(this.action);
	}

	getClosedText() {
		const closedText: any = this.actionService.getClosedTextForAction(this.action);
		if (!this.selectedRadioOption) {
			this.selectedRadioOption = {
				label: '',
				value: ''
			};
		}
		if (!this.selectedSubCategory) {
			this.selectedSubCategory = {
				label: '',
				value: ''
			};
		}

		this.closedTextCommand = closedText.command;

		const property: ActionParameterValueData = this.getActionParameterValue(closedText.prop);
		this.closedTextProperty = property.value;
		if (property.hasDatasource) {
			this.propertyHasDataSource = true;
		} else {
			this.propertyHasDataSource = false;
		}

		const value: ActionParameterValueData = this.getActionParameterValue(closedText.val, closedText.method);
		this.closedTextValue = value.value;
		if (value.hasDatasource) {
			this.valueHasDataSource = true;
		} else {
			this.valueHasDataSource = false;
		}
	}

	getWidgetLabel(val: any): string {
		let ret = '';

		if (typeof val === 'object' && val && typeof val.datasource !== 'undefined' && typeof val.datasource.defaultValue !== 'undefined') {
			val = val.datasource.defaultValue;
		}

		ret = this.entityLabelsByExtendedId['Widget:' + val];
		return ret;
	}

	objectHasDatasource(val: any): boolean {
		if (typeof val === 'object' && val && typeof val.datasource !== 'undefined' && typeof val.datasource.defaultValue !== 'undefined') {
			return true;
		} else {
			return false;
		}
	}

	setDefaultParameters(num: number, values?: any[]) { // num is the number of parameters the action needs
		this.action.parameters = this.action.parameters || [];
		for (; num > 0; num--) {
			const i = num - 1;
			this.action.parameters[i] = this.action.parameters[i] || {
				name: 'p_' + i,
				value: values?.[i] ?? ''
			};
		}
	}

	private updateOptions(keepValues = false, propertyChanged = false) {
		this.category = this.actionService.getCategoryForAction(this.action);
		this.shortText = this.actionService.getShortTextForAction(this.action);
		const localstorageKeyOptions = this.interactivityEditorService.localstorageKeys.map(v => ({ label: v, value: v }));
		const localstorageValueOptions = this.interactivityEditorService.localstorageValues.map(v => ({ label: v, value: v }));

		const chapterOptions = this.interactivityEditorService.widgetsAsOptions.filter(w => w && w.type === 'chapter');

		let widgetType: string = null;
		const param0 = this.action && this.action.parameters && this.action.parameters[0];
		const param1 = this.action && this.action.parameters && this.action.parameters[1];

		if (keepValues === false) {
			this.action.parameters = [];
		}

		this.parameterComponents = [];
		this.commandComponent = null;
		this.actionText = '';
		switch (this.category) {
			default:
				this.subCategoryList = [];
				this.radioOptions = [];
				break;

			case 'navigate':
				this.setSubCategoryList([{
					label: this.translate.instant('CLIP'),
					value: 'load',
					entity: 'player',
					commands: ['load'],
					icon: 'film'
				},
				{
					label: this.translate.instant('PROJECT'),
					value: 'loadProject',
					entity: 'player',
					commands: ['loadProject'],
					icon: 'paper-plane'
				},
				{
					label: this.translate.instant('WEBPAGE'),
					value: 'navigation',
					entity: 'navigation',
					commands: [''],
					icon: 'link',
				},
				{
					label: this.translate.instant('SEEK_TO'),
					value: 'seek',
					entity: 'player',
					commands: ['seek'],
					icon: 'clock-o',
				},
				{
					label: this.translate.instant('SEEK_TO_CHAPTER'),
					value: 'seekchapter',
					commands: ['seekchapter'],
					entity: 'player',
					icon: 'paragraph',
				},
				{
					label: this.translate.instant('DOWNLOAD'),
					value: 'download',
					entity: 'download',
					commands: [''],
					icon: 'download',
				}]);
				this.radioOptions = [];


				switch (this.selectedSubCategory.value) {
					case 'load':
						this.setDefaultParameters(2);
						this.parameterComponents = [{
							name: 'Entity selector',
							label: this.translate.instant('CLIP'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								entityType: 'commonclip',
								filterSet: [{
									filters: [{
										type: 'mediaclip',
										field: 'mediatype',
										operator: 'is',
										value: 'video'
									}]
								}]
							},
							iType: 'entitySelector'
						},
						{
							name: 'Time selector selector',
							label: this.translate.instant('OFFSET_TIME'),
							controlType: 'param',
							configuration: {
								useDataSource: true
							},
							paramIndex: 1,
							iType: 'timeCodeSelector'
						}];
						break;

					case 'loadProject':
						this.setDefaultParameters(2);
						this.parameterComponents = [{
							name: 'Entity selector',
							label: this.translate.instant('PROJECT'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								entityType: 'project',
								filterSet: []
							},
							iType: 'entitySelector'
						},
						{
							name: 'Time selector selector',
							label: this.translate.instant('OFFSET_TIME'),
							controlType: 'param',
							configuration: {
								useDataSource: true
							},
							paramIndex: 1,
							iType: 'timeCodeSelector'
						}];
						break;

					case 'navigation':
						this.setDefaultParameters(3, ['https://', '_blank']);
						this.parameterComponents = [{
							name: 'Text',
							label: this.translate.instant('URL'),
							controlType: 'param',
							paramIndex: 0,
							iType: 'text'
						},
						{
							name: 'Frame target selector',
							label: this.translate.instant('TARGET'),
							controlType: 'param',
							paramIndex: 1,
							configuration: {
								options: [
									{
										label: '_blank',
										value: '_blank'
									},
									{
										label: '_parent',
										value: '_parent'
									},
									{
										label: '_self',
										value: '_self'
									},
									{
										label: '_top',
										value: '_top'
									},
									{
										label: 'framename',
										value: 'framename'
									}
								]
							},
							iType: 'select'
						}];

						if (this.action.parameters && this.action.parameters[1] && this.action.parameters[1].value === 'framename') {
							// add extra parameter field for framename
							this.parameterComponents.push({
								name: 'Text',
								label: this.translate.instant('FRAMENAME'),
								controlType: 'param',
								paramIndex: 2,
								iType: 'text'
							});
						}
						break;

					case 'seek':
						this.setDefaultParameters(1, [0]);
						this.parameterComponents = [{
							name: 'Time selector selector',
							label: this.translate.instant('TIME_OFFSET'),
							placeholder: this.translate.instant('LEAVE_BLANK_TO_DISABLE'),
							controlType: 'param',
							paramIndex: 0,
							iType: 'timeCodeSelector'
						}];
						break;

					case 'seekchapter':
						this.setDefaultParameters(1);
						this.parameterComponents = [{
							name: 'Chapter selector',
							label: this.translate.instant('CHAPTER'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								options: chapterOptions
							},
							iType: 'entitySelector'
						}];
						break;

					case 'download':
						this.setDefaultParameters(1);
						this.parameterComponents = [{
							name: 'Entity selector',
							label: this.translate.instant('DOWNLOAD'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								entityType: 'commonclip'
							},
							iType: 'entitySelector'
						}];
						break;
				}


				break;

			case 'player':
				// wrong icons!
				this.setSubCategoryList([{
					label: this.translate.instant('PLAYER'),
					value: 'player',
					entity: 'player',
					commands: ['pause', 'play'],
					icon: 'play'
				},
				{
					label: this.translate.instant('FULLSCREEN'),
					value: 'fullscreen',
					entity: 'player',
					commands: ['fullscreen', 'retractFullscreen', 'togglefullscreen'],
					icon: 'expand'
				},
				{
					label: this.translate.instant('VOLUME'),
					value: 'volume',
					entity: 'player',
					commands: ['volume', 'unmute', 'mute', 'programVolume', 'programMute', 'programUnmute'],
					icon: 'volume-up'
				},
				{
					label: this.translate.instant('BUTTONS'),
					value: 'buttons',
					entity: 'player',
					commands: ['hide', 'show', 'disable', 'enable'],
					icon: 'cog'
				},
				{
					label: this.translate.instant('SUBTITLES'),
					value: 'subtitles',
					entity: 'player',
					commands: ['setsubtitle', 'showsubtitlelayer', 'hidesubtitlelayer'],
					icon: 'align-center'
				},
				{
					label: this.translate.instant('AUDIO_TRACKS'),
					value: 'audiotracks',
					entity: 'player',
					commands: ['setaudiotrackbyiso', 'setaudiotrack'],
					icon: 'hard-of-hearing'
				},
				{
					label: this.translate.instant('SHARING'),
					value: 'share',
					entity: 'player',
					commands: ['sharebyemail', 'shareonfacebook', 'shareontwitter', 'shareonlinkedin',
						'shareongoogleplus', 'shareonpinterest', 'shareonwhatsapp'],
					icon: 'share'
				},
				{
					label: this.translate.instant('OTHER'),
					value: 'other',
					entity: 'player',
					commands: ['skipAd'],
					icon: 'list-ul'
				}]);

				switch (this.selectedSubCategory.value) {
					default:
						this.radioOptions = [];
						break;

					case 'player':
						this.radioOptions = [
							{
								label: this.translate.instant('PAUSE'),
								icon: 'pause',
								entity: 'player',
								commands: ['pause'],
								value: 'pause'
							},
							{
								label: this.translate.instant('PLAY'),
								icon: 'play',
								entity: 'player',
								commands: ['play'],
								value: 'play'
							}
						];
						break;

					case 'fullscreen':
						this.radioOptions = [
							{
								label: this.translate.instant('FULLSCREEN'),
								icon: 'expand',
								entity: 'player',
								commands: ['fullscreen'],
								value: 'fullscreen'
							},
							{
								label: this.translate.instant('RETRACT'),
								icon: 'compress',
								entity: 'player',
								commands: ['retractFullscreen'],
								value: 'retractFullscreen'
							},
							{
								label: this.translate.instant('TOGGLE'),
								icon: 'exchange',
								entity: 'player',
								commands: ['togglefullscreen'],
								value: 'togglefullscreen'
							}
						];
						break;

					case 'volume':
						this.radioOptions = [
							{
								label: this.translate.instant('MASTER'),
								icon: '',
								value: 'master',
								entity: 'player',
								commands: ['volume', 'mute', 'unmute']
							},
							{
								label: this.translate.instant('MEDIA'),
								icon: '',
								value: 'media',
								entity: 'player',
								commands: ['programVolume', 'programMute', 'programUnmute']
							}
						];
						this.setDefaultParameters(1, ['1']);
						this.parameterComponents = [{
							name: 'Mute button and slider',
							label: this.translate.instant('VOLUME'),
							controlType: 'param',
							paramIndex: 0,
							iType: 'slider'
						}];
						break;

					case 'buttons':
						this.radioOptions = [
							{
								label: this.translate.instant('HIDE'),
								icon: '',
								entity: 'player',
								commands: ['hide'],
								value: 'hide'
							},
							{
								label: this.translate.instant('SHOW'),
								icon: '',
								entity: 'player',
								commands: ['show'],
								value: 'show'
							},
							{
								label: this.translate.instant('DISABLE'),
								icon: '',
								entity: 'player',
								commands: ['disable'],
								value: 'disable'
							},
							{
								label: this.translate.instant('ENABLE'),
								icon: '',
								entity: 'player',
								commands: ['enable'],
								value: 'enable'
							}
						];
						this.setDefaultParameters(1);
						this.parameterComponents = [{
							name: 'Player button selector',
							label: this.translate.instant('CONTROL'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								hideIcons: true,
								options: [
									{
										label: this.translate.instant('CONTROL_BAR'),
										icon: 'window-minimize', // desktop?
										value: 'controlBar'
									},
									{
										label: this.translate.instant('PROGRESS_BAR'),
										icon: 'sliders', // ?
										value: 'progressBar'
									},
									{
										label: this.translate.instant('VOLUME_SLIDER'),
										icon: 'sliders', // ?
										value: 'volumeSlider'
									},
									{
										label: this.translate.instant('CAST_BUTTON'),
										icon: 'square', // ?
										value: 'castButton'
									},
									{
										label: this.translate.instant('FULL_SCREEN_BUTTON'),
										icon: 'square', // expand?
										value: 'fullscreenButton'
									},
									{
										label: this.translate.instant('MUTE_BUTTON'),
										icon: 'square', // volume-off?
										value: 'muteButton'
									},
									{
										label: this.translate.instant('PLAY_PAUSE_BUTTON'),
										icon: 'square', // play?
										value: 'playPauseButton'
									},
									{
										label: this.translate.instant('SETTINGS_BUTTON'),
										icon: 'square', // cog?
										value: 'settingsButton'
									},
									{
										label: this.translate.instant('SHARE_BUTTON'),
										icon: 'square', // share?
										value: 'shareButton'
									},
									{
										label: this.translate.instant('SUBTITLE_SELECTOR'),
										icon: 'list-alt', // cc?
										value: 'languageButton'
									},
									{
										label: this.translate.instant('AUDIO_TRACK_SELECTOR'),
										icon: 'list-alt', // ?
										value: 'audioTrackButton'
									},
									{
										label: this.translate.instant('QUALITY_SELECTOR'),
										icon: 'list-alt', // ?
										value: 'hdButton'
									},
									{
										label: this.translate.instant('LOGO'),
										icon: 'picture-o',
										value: 'logo'
									},
									{
										label: this.translate.instant('TIME_DISPLAY'),
										icon: 'info', // ?
										value: 'timeDisplay'
									}
								]
							},
							iType: 'entitySelector'
						}];
						break;

					case 'subtitles':
						this.radioOptions = [
							{
								label: this.translate.instant('SHOW'),
								icon: '',
								entity: 'player',
								commands: ['showsubtitlelayer'],
								value: 'showsubtitlelayer'
							},
							{
								label: this.translate.instant('HIDE'),
								icon: '',
								entity: 'player',
								commands: ['hidesubtitlelayer'],
								value: 'hidesubtitlelayer'
							},
							{
								label: this.translate.instant('SUBTITLE_LANGUAGE'),
								icon: '',
								entity: 'player',
								commands: ['setsubtitle'],
								value: 'setsubtitle'
							}
						];
						switch (this.action.command) {
							case 'showsubtitlelayer':
							case 'hidesubtitlelayer':
								this.setDefaultParameters(0);
								break;

							case 'setsubtitle':
								this.setDefaultParameters(1);
								this.parameterComponents = [{
									name: 'Language selector',
									label: this.translate.instant('LANGUAGE'),
									controlType: 'param',
									paramIndex: 0,
									configuration: {
										useDataSource: true,
										options: this.languageOptions
									},
									iType: 'entitySelector'
								}];
								break;
						}
						break;

					case 'audiotracks':
						this.radioOptions = [];
						this.setDefaultParameters(1);
						this.parameterComponents = [{
							name: 'Language selector',
							label: this.translate.instant('LANGUAGE'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								options: this.languageOptions
							},
							iType: 'entitySelector'
						}];
						break;

					case 'share':
						this.radioOptions = [];
						this.setDefaultParameters(0);
						this.commandComponent = {
							name: 'Player share option selector',
							label: this.translate.instant('PLATFORM'),
							configuration: {
								useDataSource: true,
								options: [
									{
										label: 'Facebook',
										icon: 'facebook-official',
										value: 'shareonfacebook'
									},
									{
										label: 'Pinterest',
										icon: 'pinterest',
										value: 'shareonpinterest'
									},
									{
										label: 'Mail',
										icon: 'envelope',
										value: 'sharebyemail'
									},
									{
										label: 'Twitter',
										icon: 'twitter',
										value: 'shareontwitter'
									},
									{
										label: 'Linkedin',
										icon: 'linkedin-square',
										value: 'shareonlinkedin'
									},
									{
										label: 'Whatsapp',
										icon: 'whatsapp',
										value: 'shareonwhatsapp'
									}
								]
							},
							iType: 'entitySelector'
						};
						break;

					case 'other':
						this.radioOptions = [];
						this.setDefaultParameters(0);
						this.commandComponent = {
							name: 'Player other option selector',
							label: this.translate.instant('PLAYER_COMMAND'),
							configuration: {
								useDataSource: true,
								extendable: true,
								options: [
									{
										label: 'Skip ad',
										value: 'skipAd'
									}
								]
							},
							iType: 'entitySelector'
						};
						break;
				}


				break;


			case 'script':
				this.subCategoryList = [];
				this.radioOptions = [];
				this.setDefaultParameters(1);
				this.parameterComponents = [{
					name: 'Script editor',
					label: this.translate.instant('SCRIPT'),
					controlType: 'param',
					paramIndex: 0,
					iType: 'scriptEditor',
					configuration: {
						useMacroSuggestions: true,
						disabledMacroHotkey: true
					}
				}];
				break;

			case 'localstorage':
				this.subCategoryList = [];
				this.radioOptions = [
					{
						label: this.translate.instant('SET'),
						icon: '',
						entity: 'localstorage',
						commands: ['setItem'],
						value: 'set'
					},
					{
						label: this.translate.instant('FROM_FORM_FIELD'),
						icon: '',
						entity: 'localstorage',
						commands: ['setItemWithWidgetProperty'],
						value: 'formField'
					},
					{
						label: this.translate.instant('CLEAR'),
						icon: '',
						entity: 'localstorage',
						commands: ['removeItem'],
						value: 'clear'
					}
				];

				switch (this.action.command) {
					case 'setItem':
						this.setDefaultParameters(2);
						this.parameterComponents = [{
							name: 'Local storage name selector',
							label: this.translate.instant('NAME'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								options: localstorageKeyOptions,
								extendable: true
							},
							iType: 'entitySelector'
						},
						{
							name: 'Value',
							label: this.translate.instant('VALUE'),
							controlType: 'param',
							paramIndex: 1,
							configuration: {
								useDataSource: true,
								options: localstorageValueOptions,
								extendable: true
							},
							iType: 'entitySelector'
						}];

						break;

					case 'setItemWithWidgetProperty':
						// is this option needed? We can set the value of the second parameter programmatically?
						if (param1 && typeof param1.value === 'string') {
							widgetType = this.interactivityEditorService.getWidgetTypeForId(param1.value);
						} // NB second parameter!

						this.setDefaultParameters(3, [null, null, 'value']);
						this.parameterComponents = [{
							name: 'Local storage name selector',
							label: this.translate.instant('NAME'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								options: localstorageKeyOptions,
								extendable: true
							},
							iType: 'entitySelector'
						},
						{
							name: 'Widget selector',
							label: this.translate.instant('WIDGET'),
							configuration: {
								useDataSource: true,
								options: this.interactivityEditorService.widgetsAsOptions
							},
							controlType: 'param',
							paramIndex: 1,
							iType: 'entitySelector'
						},
						{
							name: 'Widget property selector',
							label: this.translate.instant('PROPERTY'),
							configuration: {
								useDataSource: false,
								options: this.actionService.getWidgetPropOptionsForType(widgetType),
							},
							controlType: 'param',
							paramIndex: 2,
							iType: 'entitySelector'
						}];

						break;

					case 'removeItem':
						this.setDefaultParameters(1);
						this.parameterComponents = [{
							name: 'Local storage name selector',
							label: this.translate.instant('NAME'),
							controlType: 'param',
							paramIndex: 0,
							configuration: {
								useDataSource: true,
								options: localstorageKeyOptions,
								extendable: true
							},
							iType: 'entitySelector'
						}];

						break;
				}


				break;

			case 'widget':
				if (param0 && typeof param0.value === 'string') {
					widgetType = this.interactivityEditorService.getWidgetTypeForId(param0.value);
				} // NB first parameter!

				this.subCategoryList = [];
				this.radioOptions = [{
					label: this.translate.instant('WIDGET'),
					icon: '',
					entity: 'widget',
					commands: ['callMethod', '__ANY'],
					value: 'widget'
				},
				{
					label: this.translate.instant('WIDGETS_BY_TAG'),
					icon: '',
					entity: 'group',
					commands: ['callMethod', '__ANY'],
					value: 'group'
				}];

				if (this.action.entity === 'widget') {
					this.parameterComponents = [
						{
							name: 'Widget selector',
							label: this.translate.instant('WIDGET'),
							configuration: {
								useDataSource: true,
								options: this.interactivityEditorService.widgetsAsOptions
							},
							controlType: 'param',
							paramIndex: 0,
							iType: 'entitySelector'
						},
						{
							name: 'Widget control selector',
							label: this.translate.instant('CONTROL'),
							configuration: {
								useDataSource: false,
								options: this.actionService.getWidgetControlOptionsForType(widgetType),
							},
							controlType: 'param',
							paramIndex: 1,
							iType: 'entitySelector'
						}
					];
				} else if (this.action.entity === 'group') {
					this.parameterComponents = [
						{
							name: 'Group selector',
							label: this.translate.instant('TAG'),
							configuration: {
								useDataSource: true,
								options: this.allWidgetTags
							},
							controlType: 'param',
							paramIndex: 0,
							iType: 'entitySelector'
						},
						{
							name: 'Group control selector',
							label: this.translate.instant('CONTROL'),
							configuration: {
								useDataSource: false,
								options: this.actionService.getWidgetControlOptionsForType('group'),
							},
							controlType: 'param',
							paramIndex: 1,
							iType: 'entitySelector'
						}
					];
				}

				let iType = null;
				if (param1 && param1.value) {
					iType = this.actionService.getPropertyValueType(param1.value.toString());
				}

				const propertyConfiguration: any = {};

				if (param1 && (param1.value === 'setValue')) {
					const extraParameters: any[] = [{
						name: 'value',
						label: this.translate.instant('VALUE'),
						controlType: 'param',
						configuration: {
							useDataSource: true
						},
						paramIndex: 2,
						iType: 'text'
					}];
					const merged = this.parameterComponents.concat(extraParameters);
					this.setDefaultParameters(merged.length);
					this.parameterComponents = merged;
				} else if (param1 && (param1.value === 'transition' || param1.value === 'customTransition')) {
					let extraParameters: any[] = [{
						name: 'time selector',
						label: this.translate.instant('TIME_IN_SECONDS'),
						controlType: 'param',
						configuration: {
							useDataSource: true
						},
						paramIndex: 2,
						iType: 'number'
					}];
					if (param1.value === 'customTransition') {
						extraParameters = extraParameters.concat([
							{
								name: 'left selector',
								label: this.translate.instant('LEFT'),
								controlType: 'param',
								configuration: {},
								paramIndex: 3,
								iType: 'text'
							},
							{
								name: 'top selector',
								label: this.translate.instant('TOP'),
								controlType: 'param',
								configuration: {},
								paramIndex: 4,
								iType: 'text'
							},
							{
								name: 'width selector',
								label: this.translate.instant('WIDTH'),
								controlType: 'param',
								configuration: {},
								paramIndex: 5,
								iType: 'text'
							},
							{
								name: 'height selector',
								label: this.translate.instant('HEIGHT'),
								controlType: 'param',
								configuration: {},
								paramIndex: 6,
								iType: 'text'
							},
							{
								name: 'transition fade selector',
								label: this.translate.instant('FADE'),
								controlType: 'param',
								configuration: {
									options: [
										{
											label: this.translate.instant('NONE'),
											value: 'none'
										},
										{
											label: this.translate.instant('FADE_IN'),
											value: 'fade in'
										},
										{
											label: this.translate.instant('FADE_OUT'),
											value: 'fade out'
										}
									]
								},
								paramIndex: 7,
								iType: 'select'
							},
							{
								name: 'transition easing selector',
								label: this.translate.instant('TRANSITION_EASING'),
								controlType: 'param',
								configuration: {
									options: [
										{
											label: this.translate.instant('NONE'),
											value: 'none'
										},
										{
											label: this.translate.instant('LINEAR'),
											value: 'lineair'
										},
										{
											label: this.translate.instant('EXPONENTIAL'),
											value: 'exponential'
										}
									]
								},
								paramIndex: 8,
								iType: 'select'
							}]);
					} else {
						extraParameters = extraParameters.concat([{
							name: 'transition type selector',
							label: this.translate.instant('TRANSITION_TYPE'),
							controlType: 'param',
							configuration: {
								options: [
									{
										label: this.translate.instant('NONE'),
										value: 'none'
									},
									{
										label: this.translate.instant('SLIDE_IN_FROM_LEFT'),
										value: 'Slide in from left'
									},
									{
										label: this.translate.instant('SLIDE_IN_FROM_RIGHT'),
										value: 'Slide in from right'
									},
									{
										label: this.translate.instant('SLIDE_IN_FROM_TOP'),
										value: 'Slide in from top'
									},
									{
										label: this.translate.instant('SLIDE_IN_FROM_BOTTOM'),
										value: 'Slide in from bottom'
									},
									{
										label: this.translate.instant('SLIDE_OUT_TO_LEFT'),
										value: 'Slide out to left'
									},
									{
										label: this.translate.instant('SLIDE_OUT_TO_RIGHT'),
										value: 'Slide out to right'
									},
									{
										label: this.translate.instant('SLIDE_OUT_TO_TOP'),
										value: 'Slide out to top'
									},
									{
										label: this.translate.instant('SLIDE_OUT_TO_BOTTOM'),
										value: 'Slide out to bottom'
									}
								]
							},
							paramIndex: 3,
							iType: 'select'
						},
						{
							name: 'transition fade selector',
							label: this.translate.instant('FADE'),
							controlType: 'param',
							configuration: {
								options: [
									{
										label: this.translate.instant('NONE'),
										value: 'none'
									},
									{
										label: this.translate.instant('FADE_IN'),
										value: 'fade in'
									},
									{
										label: this.translate.instant('FADE_OUT'),
										value: 'fade out'
									}
								]
							},
							paramIndex: 4,
							iType: 'select'
						},
						{
							name: 'transition easing selector',
							label: this.translate.instant('TRANSITION_EASING'),
							controlType: 'param',
							configuration: {
								options: [
									{
										label: this.translate.instant('NONE'),
										value: 'none'
									},
									{
										label: this.translate.instant('LINEAR'),
										value: 'lineair'
									},
									{
										label: this.translate.instant('EXPONENTIAL'),
										value: 'exponential'
									}
								]
							},
							paramIndex: 5,
							iType: 'select'
						}]);
					}

					const merged = this.parameterComponents.concat(extraParameters);
					this.setDefaultParameters(merged.length);
					this.parameterComponents = merged;
				} else if (param1 && param1.value === 'volumetransition') {
					const extraParameters: any[] = [{
						name: 'time selector',
						label: this.translate.instant('TIME_IN_SECONDS'),
						controlType: 'param',
						configuration: {
							useDataSource: true
						},
						paramIndex: 2,
						iType: 'number'
					},
					{
						name: 'fade selector',
						label: this.translate.instant('FADE'),
						controlType: 'param',
						configuration: {
							options: [
								{
									label: this.translate.instant('FADE_OUT'),
									value: 'fade out'
								},
								{
									label: this.translate.instant('FADE_IN'),
									value: 'fade in'
								}
							]
						},
						paramIndex: 3,
						iType: 'select'
					},
					{
						name: 'endVolume',
						label: this.translate.instant('VOLUME'),
						controlType: 'param',
						configuration: {
							value_min: 0,
							value_max: 100,
							value_step: 5
						},
						paramIndex: 4,
						iType: 'slider'
					}];
					const merged = this.parameterComponents.concat(extraParameters);
					this.setDefaultParameters(merged.length);
					this.parameterComponents = merged;
				} else {
					if (iType) {
						if (iType.startsWith('entitySelector')) {
							propertyConfiguration.entityType = 'commonclip';
							if (iType === 'entitySelector:image') {
								propertyConfiguration.filterSet = [{ filters: [{ type: 'mediaclip', field: 'mediatype', operator: 'is', value: 'image' }] }];
							}
							iType = iType.split(':')[0];
						} else if (iType === '100slider') {
							iType = 'slider';
							propertyConfiguration.value_min = 0;
							propertyConfiguration.value_max = 100;
							propertyConfiguration.value_step = 5;
						} else if (iType === 'slider') {
							propertyConfiguration.formatter = (val) => `${Math.round(val * 100)}%`;
						}
						propertyConfiguration.useDataSource = true;
						this.setDefaultParameters(3);
						this.parameterComponents.push(
							{
								name: 'Any value?',
								label: this.translate.instant('VALUE'),
								controlType: 'param',
								configuration: propertyConfiguration,
								paramIndex: 2,
								iType
							}
						);
					} else {
						this.setDefaultParameters(2);
					}
				}

				break;

			case 'send':
				this.subCategoryList = [];
				this.radioOptions = [{
					label: this.translate.instant('MESSAGE'),
					icon: '',
					entity: 'message',
					commands: ['', '__ANY'],
					value: 'message'
				},
				{
					label: this.translate.instant('LOG_STATS'),
					icon: '',
					entity: 'analytics',
					commands: ['logevent'],
					value: 'logevent'
				},
				{
					label: this.translate.instant('FORM'),
					icon: '',
					entity: 'form',
					commands: ['submit'],
					value: 'form'
				}];

				switch (this.action.entity) {
					case 'message':
						this.setDefaultParameters(0);
						this.commandComponent = {
							name: 'Message editor',
							label: this.translate.instant('MESSAGE'),
							iType: 'messageSelector',
							configuration: {
								useMacroSuggestions: true,
							}
						};
						break;
					case 'analytics':
						this.setDefaultParameters(2);
						this.parameterComponents = [{
							name: 'Stats event key selector',
							label: this.translate.instant('CUSTOM_EVENT_KEY'),
							controlType: 'param',
							paramIndex: 0,
							iType: 'eventSelector',
							configuration: {
								useMacroSuggestions: true,
							}
						},
						{
							name: 'Stats event value selector',
							label: this.translate.instant('CUSTOM_EVENT_VALUE'),
							controlType: 'param',
							paramIndex: 1,
							iType: 'eventSelector',
							configuration: {
								useMacroSuggestions: true,
							}
						}];
						break;
					case 'form':
						this.actionText = 'SUBMIT_FORM';
						break;
				}

				break;
		}

		// default radio option
		if (this.radioOptions && this.radioOptions.length) {
			let ind = 0;
			this.radioOptions.forEach((option, i) => {
				if (this.action && this.action.command !== undefined &&
					(!option.entity || option.entity === this.action.entity) && (
					(
						option.commands && option.commands.indexOf(this.action.command) !== -1) ||
						this.action.command === option.value ||
						option.commands.indexOf('__ANY') !== -1
				)
				) {
					ind = i;
				}
			});
			this.selectedRadioOption = this.radioOptions[ind];
			this.selectedRadioValue = this.selectedRadioOption.value;
		}
		this.getClosedText();
		this.stateChanges.next();
	}

	private getActionParameterValue(val: string, method?: any): ActionParameterValueData {
		// console.log('get action parameter', val, this.parameterComponents);
		const parameterComponents: any[] = [];
		if (this.parameterComponents) {
			this.parameterComponents.forEach(comp => {
				parameterComponents[comp.paramIndex] = comp; // don't ask...
			});
		}

		let configuration: any;
		let entityType: string;
		let option: any;
		const val_parts: any = val && val.split(';') || [];
		for (let i = 0, l = val_parts.length; i < l; i++) {
			switch (val_parts[i]) {
				case 'radio':
					val_parts[i] = this.selectedRadioOption.label;
					break;
				case 'param_0':
					val_parts[i] = this.action.parameters[0] ? this.action.parameters[0].value as string : '';
					if (method) {
						val_parts[i] = method(val_parts[i], this.action);
					}
					configuration = parameterComponents[0] && parameterComponents[0].configuration;
					break;
				case 'param_1':
					val_parts[i] = this.action.parameters[1] ? this.action.parameters[1].value as string : '';
					if (method) {
						val_parts[i] = method(val_parts[i], this.action);
					}
					configuration = parameterComponents[1] && parameterComponents[1].configuration;
					break;
				case 'param_2':
					val_parts[i] = this.action.parameters[2] ? this.action.parameters[2].value as string : '';
					if (method) {
						val_parts[i] = method(val_parts[i], this.action);
					}
					configuration = parameterComponents[2] && parameterComponents[2].configuration;
					break;
				case 'subCat':
					val_parts[i] = this.selectedSubCategory.value;
					break;
				case 'command':
					val_parts[i] = this.action.command;
					break;
			}

			if (configuration) {
				entityType = configuration.entityType;
				option = configuration.options && configuration.options.find((item: any) => (item && item.value === val_parts[i]));
				if (option && option.label) {
					val_parts[i] = option.label;
				} else if (entityType && this.actionService.getEntityLabelForExtendedId(entityType + ':' + val_parts[i]) !== undefined) {
					val_parts[i] = this.actionService.getEntityLabelForExtendedId(entityType + ':' + val_parts[i]);
				}
				if (val_parts[i] && val_parts[i].length > 24) {
					val_parts[i] = val_parts[i].substring(0, 24) + '...';
				} // NB!
				if (typeof configuration.formatter === 'function') {
					val_parts[i] = configuration.formatter(val_parts[i]);
				}
			}
		}

		const ret: ActionParameterValueData = {
			value: '',
			hasDatasource: false
		};
		for (let i = 0, l = val_parts.length; i < l; i++) {
			if (this.objectHasDatasource(val_parts[i])) {
				val_parts[i] = val_parts[i].datasource.defaultValue;
				ret.hasDatasource = true;
			}
		}
		ret.value = val_parts.join(' ');
		return ret;
	}

	private setSubCategoryList(values: IActionListItem[]) {
		this.subCategoryList = values;
		let ind = 0;
		this.subCategoryList.forEach((item, i) => {
			if (this.action &&
				this.action.entity && item.entity === this.action.entity &&
				this.action.command !== undefined && item.commands && item.commands.indexOf(this.action.command) !== -1) {
				ind = i;
			}
		});

		// set default subCategory:
		if (this.subCategoryList && this.subCategoryList.length) {
			this.selectedSubCategory = this.subCategoryList[ind];
		}
	}

	private dispatchChange(): void {
		this.actionChanged.emit(null);
	}
}
